<template>
  <div
    class="payflow-mobile"
    :class="{ 'payflow-mobile--with-footer': showPaymentPanel && needsPayment }"
  >
    <header class="payflow-mobile__header">
      <div class="payflow-mobile__header-nav">
        <div class="payflow-mobile__brand">
          <div class="payflow-mobile__brand-logo-wrap">
            <img
              v-if="logoUrl"
              :src="logoUrl"
              :alt="shopName"
              class="payflow-mobile__brand-logo"
            />
            <div v-else class="payflow-mobile__brand-logo-placeholder">
              <v-icon color="#0066FF" size="20">mdi-store-outline</v-icon>
            </div>
          </div>
          <h1 class="payflow-mobile__appbar-title">{{ shopName }}</h1>
        </div>

        <div class="payflow-mobile__header-actions">
          <button
            type="button"
            class="payflow-mobile__icon-btn"
            :aria-label="$t('invoicePrint')"
            @click="$emit('print-invoice')"
          >
            <v-icon color="#455A64" size="22">mdi-printer</v-icon>
          </button>

          <button type="button" class="payflow-mobile__icon-btn" @click="goBack">
            <v-icon color="#455A64" size="22">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </header>

    <div class="payflow-mobile__customer-panel">
      <div class="payflow-mobile__header-greeting">
        <p class="payflow-mobile__greeting">
          <template v-if="customerDisplayName">
            {{ $t("DearCustomer") }}
            <strong class="payflow-mobile__customer-name">{{
              customerDisplayName
            }}</strong
            >،
            {{ $t("thanksToBuy") }}
          </template>
          <template v-else> {{ $t("DearCustomer") }} ، {{ $t("thanksToBuy") }} </template>
        </p>
      </div>

      <div v-if="hasCustomerMeta" class="payflow-mobile__customer-strip">
        <div class="payflow-customer-meta payflow-customer-meta--compact">
          <div
            v-if="displayNumber || customerContact"
            class="payflow-customer-meta__row payflow-customer-meta__row--split"
          >
            <div v-if="customerContact" class="payflow-customer-meta__cell">
              <v-icon x-small color="#90A4AE" class="ml-1">
                {{ customerContactIcon }}
              </v-icon>
              <span class="payflow-customer-meta__label"
                >{{ customerContactLabel }}:</span
              >
              <span
                class="payflow-customer-meta__value payflow-customer-meta__value--phone"
              >
                {{ customerContact }}
              </span>
            </div>
            <div v-if="displayNumber" class="payflow-customer-meta__cell">
              <span class="payflow-customer-meta__label">{{ $t("number") }}:</span>
              <span
                class="payflow-customer-meta__value payflow-customer-meta__value--invoice"
              >
                {{ displayNumber }}
              </span>
            </div>
          </div>

          <div v-if="customer && customer.address" class="payflow-customer-meta__row">
            <v-icon x-small color="#90A4AE" class="ml-1">mdi-map-marker-outline</v-icon>
            <span class="payflow-customer-meta__label">{{ $t("address") }}:</span>
            <span
              class="payflow-customer-meta__value payflow-customer-meta__value--address"
            >
              {{ customer.address }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div ref="scrollRoot" class="payflow-mobile__scroll payflow-scrollbar">
      <main class="payflow-mobile__body">
        <section class="payflow-mobile__products">
          <article v-for="(item, index) in items" :key="index" class="payflow-item-card">
            <div class="payflow-item-card__image-wrap">
              <img
                :src="getProductImage(item)"
                :alt="item.product.name"
                class="payflow-item-card__image"
              />
            </div>
            <div class="payflow-item-card__content">
              <h2 class="payflow-item-card__name">{{ item.product.name }}</h2>
              <p class="payflow-item-card__meta">
                {{ $numberFormat(parseFloat(item.count)) }}
                ×
                {{ $numberFormat(parseFloat(item.fee)) }}
                {{ currencyName }}
              </p>
              <p class="payflow-item-card__total">
                {{ $numberFormat(getLineTotal(item)) }}
                {{ currencyName }}
              </p>
            </div>
          </article>
        </section>

        <section class="payflow-receipt">
          <div class="payflow-receipt__inner">
            <div class="payflow-summary-row">
              <span class="payflow-summary-row__label">{{ $t("sum") }}:</span>
              <span class="payflow-summary-row__value">
                {{ $numberFormat(totals.fee || 0) }} {{ currencyName }}
              </span>
            </div>
            <div class="payflow-summary-row">
              <span class="payflow-summary-row__label">
                {{ $t("tax") }}
                <template v-if="taxPercent > 0"> ({{ taxPercent }}٪)</template>:
              </span>
              <span class="payflow-summary-row__value">
                {{ $numberFormat(totals.tax || 0) }} {{ currencyName }}
              </span>
            </div>
            <div
              v-if="totalDiscount > 0"
              class="payflow-summary-row payflow-summary-row--discount"
            >
              <span class="payflow-summary-row__label">{{ $t("discount") }}:</span>
              <span
                class="payflow-summary-row__value payflow-summary-row__value--discount"
              >
                -{{ $numberFormat(totalDiscount) }} {{ currencyName }}
              </span>
            </div>
          </div>
        </section>

        <p v-if="content" class="payflow-mobile__content">
          <strong>{{ $t("content") }}:</strong> {{ content }}
        </p>

        <div v-if="isFullyPaid" class="payflow-paid-banner">
          <v-icon color="#43A047" size="22" class="ml-1">mdi-check-circle-outline</v-icon>
          <span>{{ $t("cleared") }}</span>
        </div>

        <div
          v-else-if="showPaymentPanel && needsPayment && availableGateways.length"
          class="payflow-mobile__gateways-area"
        >
          <div class="payflow-gateway-heading-row">
            <div class="payflow-gateway-heading-start">
              <v-icon x-small color="#78909C" class="payflow-gateway-heading__icon">
                mdi-credit-card-outline
              </v-icon>
              <h3 class="payflow-gateway-heading">{{ $t("payflowSelectGateway") }}</h3>
            </div>
          </div>
          <div class="payflow-gateway-row">
            <div
              v-for="gw in availableGateways"
              :key="gw.value"
              :class="[
                'payflow-gateway-option',
                { 'payflow-gateway-option--active': gateway === gw.value },
              ]"
              @click="$emit('select-gateway', gw.value)"
            >
              <v-icon
                v-if="gateway === gw.value"
                color="#0066FF"
                size="14"
                class="payflow-gateway-check"
              >
                mdi-check-circle
              </v-icon>
              <div class="payflow-gateway-option__logo">
                <img :src="gw.logo" :alt="gw.englishName" />
              </div>
              <div class="payflow-gateway-option__name">{{ gw.name }}</div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- print component -->
    <div class="payflow-print-source">
      <pay-flow-print-table
        ref="table"
        :headers="headers"
        :items="items"
        :content="content"
        :currency-name="currencyName"
        :totals="totals"
        :total-discount="totalDiscount"
      />
    </div>
    <div class="payflow-mobile__payable-block pb-2">
      <span class="payflow-mobile__payable-label">{{ $t("amountForPay") }}</span>
      <span class="payflow-mobile__payable-value">
        {{ $numberFormat(payableAmount) }}
        <small>{{ currencyName }}</small>
      </span>
    </div>

    <footer v-if="showPaymentPanel && needsPayment" class="payflow-mobile__bottom">
      <div class="payflow-mobile__checkout-bar">
        <v-btn
          large
          :disabled="!gateway"
          :loading="loading"
          color="#0066FF"
          class="payflow-mobile__pay-btn white--text"
          @click="$emit('pay')"
        >
          {{ $t("payment") }}
        </v-btn>
      </div>
    </footer>
  </div>
</template>

<script>
import PayFlowPrintTable from "./PayFlowPrintTable.vue";

const productPlaceholder = require("@/assets/imagePlacholder.png");

export default {
  name: "PayFlowMobile",
  components: { PayFlowPrintTable },
  props: {
    headers: { type: Array, default: null },
    items: { type: Array, default: () => [] },
    content: { type: String, default: null },
    currencyName: { type: String, default: "" },
    totals: { type: Object, required: true },
    totalDiscount: { type: Number, default: 0 },
    remainingAmount: { type: Number, default: 0 },
    availableGateways: { type: Array, default: () => [] },
    gateway: { type: String, default: null },
    loading: { type: Boolean, default: false },
    needsPayment: { type: Boolean, default: false },
    isFullyPaid: { type: Boolean, default: false },
    showPaymentPanel: { type: Boolean, default: false },
    self: { type: Object, default: null },
    customer: { type: Object, default: null },
    shopName: { type: String, default: "" },
    displayNumber: { type: String, default: "" },
  },
  computed: {
    grandTotal() {
      return this.totals.fee - this.totalDiscount + this.totals.tax;
    },
    payableAmount() {
      return this.needsPayment ? this.remainingAmount : this.grandTotal;
    },
    taxPercent() {
      if (!this.totals.fee || this.totals.fee <= 0) return 0;
      return Math.round((this.totals.tax / this.totals.fee) * 100);
    },
    logoUrl() {
      return this.self && this.self.logo ? this.self.logo.link : null;
    },
    customerDisplayName() {
      if (!this.customer || !this.customer.name) return "";
      const gender = this.customer.gender ? this.$t(this.customer.gender) + " " : "";
      return gender + this.customer.name;
    },
    hasCustomerMeta() {
      return (
        !!this.displayNumber ||
        (this.customer &&
          (this.customer.phone || this.customer.mobile || this.customer.address))
      );
    },
    customerContact() {
      if (!this.customer) return "";
      return this.customer.mobile || this.customer.phone || "";
    },
    customerContactIcon() {
      return this.customer && this.customer.mobile
        ? "mdi-cellphone"
        : "mdi-phone-outline";
    },
    customerContactLabel() {
      return this.customer && this.customer.mobile ? this.$t("mobile") : this.$t("phone");
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.runIntroScroll();
    });
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        window.close();
      }
    },
    getProductImage(item) {
      return item.product && item.product.avatar && item.product.avatar.link
        ? item.product.avatar.link
        : productPlaceholder;
    },
    getLineTotal(item) {
      return (
        parseFloat(item.fee * item.count) +
        parseFloat(item.tax) -
        parseFloat(item.discount)
      );
    },
    runIntroScroll() {
      const el = this.$refs.scrollRoot;
      if (!el) return;

      const scrollToBottom = () => {
        const top = el.scrollHeight - el.clientHeight;
        if (top <= 0) return;

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
          .matches;

        if (prefersReducedMotion) {
          el.scrollTop = top;
          return;
        }

        el.scrollTo({ top, behavior: "smooth" });
      };

      requestAnimationFrame(() => {
        setTimeout(scrollToBottom, 350);
      });
    },
  },
};
</script>

<style scoped>
.payflow-mobile {
  --payflow-page-bg: #ffffff;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
}

.payflow-mobile__header {
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e8ecef;
}

.payflow-mobile__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.payflow-mobile__customer-panel {
  flex-shrink: 0;
  padding: 6px 16px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e8ecef;
}

.payflow-mobile__header-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px 8px;
}

.payflow-mobile__brand {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.payflow-mobile__brand-logo-wrap {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payflow-mobile__brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.payflow-mobile__brand-logo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.payflow-mobile__appbar-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  text-align: right;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payflow-mobile__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.payflow-mobile__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
}

.payflow-mobile__icon-btn:active {
  background: #f5f5f5;
}

.payflow-mobile__header-greeting {
  padding: 0 0 4px;
}

.payflow-mobile__greeting {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #546e7a;
  text-align: right;
}

.payflow-mobile__customer-name {
  font-weight: 700;
  color: #212121;
  font-size: 13px;
}

.payflow-mobile__customer-strip {
  margin-bottom: 0;
  padding: 0;
  background: transparent;
}

.payflow-customer-meta--compact {
  gap: 2px;
}

.payflow-customer-meta__value--address {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.payflow-customer-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payflow-customer-meta__row {
  display: flex;
  align-items: flex-start;
  gap: 3px;
  font-size: 10px;
  line-height: 1.35;
  color: #78909c;
}

.payflow-customer-meta__row--split {
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.payflow-customer-meta__cell {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.payflow-customer-meta__value--phone {
  direction: ltr;
  unicode-bidi: plaintext;
}

.payflow-customer-meta__label {
  flex-shrink: 0;
  font-weight: 500;
  color: #90a4ae;
}

.payflow-customer-meta__value {
  color: #455a64;
  font-weight: 500;
  word-break: break-word;
}

.payflow-customer-meta__value--invoice {
  color: #0066ff;
  font-weight: 600;
}

.payflow-mobile__body {
  padding: 10px 16px 20px;
  background: var(--payflow-page-bg);
}

.payflow-mobile--with-footer .payflow-mobile__body {
  padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
}

.payflow-mobile__products {
  margin-bottom: 8px;
  padding: 2px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  background: #fafafa;
}

.payflow-mobile__gateways-area {
  margin-bottom: 8px;
}

.payflow-item-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  padding: 8px 10px;
  margin: 6px;
  box-shadow: none;
}

.payflow-item-card__image-wrap {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background: #f5f5f5;
  border: 1px solid #eeeeee;
  overflow: hidden;
}

.payflow-item-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.payflow-item-card__content {
  flex: 1;
  min-width: 0;
}

.payflow-item-card__name {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  line-height: 1.35;
}

.payflow-item-card__meta {
  margin: 0 0 4px;
  font-size: 11px;
  color: #90a4ae;
  line-height: 1.35;
}

.payflow-item-card__total {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #0066ff;
  line-height: 1.25;
}

.payflow-receipt {
  --receipt-notch-size: 25px;
  position: relative;
  flex-shrink: 0;
  margin: 10px 0 14px;
  filter: drop-shadow(0 2px 10px rgba(15, 23, 42, 0.08));
}

.payflow-receipt__inner {
  position: relative;
  padding: 14px 20px 12px;
  background: #ffffff;
  /* border: 1px solid #e8ecef; */
  border-radius: 12px;
  overflow: visible;
}

.payflow-receipt__inner::before,
.payflow-receipt__inner::after {
  content: "";
  position: absolute;
  top: 50%;
  width: var(--receipt-notch-size);
  height: var(--receipt-notch-size);
  border-radius: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: radial-gradient(
    circle at center,
    #fafafa 95%,
    #efeeee 82%,
    rgba(0, 0, 0, 0.08) 85%,
    transparent 100%
  );

  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.payflow-receipt__inner::before {
  left: 0;
  transform: translate(-50%, -50%);
  clip-path: inset(0 0 0 50%);
}

.payflow-receipt__inner::after {
  right: 0;
  transform: translate(50%, -50%);
  clip-path: inset(0 50% 0 0);
}

.payflow-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
}

.payflow-summary-row__label {
  font-size: 14px;
  font-weight: 500;
  color: #37474f;
  white-space: nowrap;
}

.payflow-summary-row__value {
  font-size: 14px;
  font-weight: 500;
  color: #263238;
  text-align: left;
  white-space: nowrap;
}

.payflow-summary-row--discount .payflow-summary-row__label,
.payflow-summary-row__value--discount {
  color: #e53935 !important;
  font-weight: 500;
}

.payflow-mobile__content {
  flex-shrink: 0;
  font-size: 12px;
  color: #546e7a;
  line-height: 1.6;
  margin: 0 0 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e8ecef;
  border-radius: 12px;
}

.payflow-gateway-heading-row {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 8px;
}

.payflow-gateway-heading-start {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
}

.payflow-gateway-heading__icon {
  flex-shrink: 0;
}

.payflow-gateway-heading {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
  text-align: right;
}

.payflow-gateway-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: stretch;
  gap: 6px;
}

.payflow-gateway-option {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  max-width: 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 6px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fff;
}

.payflow-gateway-option--active {
  border: 2px solid #0066ff !important;
  background: #f5f9ff !important;
}

.payflow-gateway-check {
  position: absolute;
  top: 2px;
  left: 2px;
}

.payflow-gateway-option__name {
  width: 100%;
  font-size: 10px;
  font-weight: 500;
  color: #616161;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payflow-gateway-option__logo {
  flex-shrink: 0;
  width: 100%;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payflow-gateway-option__logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.payflow-paid-banner {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 12px;
  padding: 14px;
  font-weight: 600;
  font-size: 14px;
}

.payflow-mobile__bottom {
  flex-shrink: 0;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #e8ecef;
  box-shadow: none;
}

.payflow-mobile__checkout-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payflow-mobile__payable-block {
  flex-shrink: 0;
  min-width: 0;
  max-width: 100%;
  text-align: center;
}

.payflow-mobile__payable-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #78909c;
  line-height: 1.3;
  margin-bottom: 2px;
}

.payflow-mobile__payable-value {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #0066ff;
  line-height: 1.2;
  white-space: nowrap;
}

.payflow-mobile__payable-value small {
  font-size: 11px;
  font-weight: 500;
  margin-right: 2px;
}

.payflow-mobile__pay-btn {
  flex: 1 1 auto;
  min-width: 0;
  border-radius: 12px !important;
  height: 48px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}

.payflow-print-source {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 1100px;
  visibility: hidden;
  pointer-events: none;
  overflow: visible;
}

.payflow-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.payflow-scrollbar::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

@media (max-height: 700px), (max-width: 375px) {
  .payflow-gateway-option {
    max-width: 76px;
    padding: 6px 4px;
    border-radius: 8px;
  }

  .payflow-gateway-option__logo {
    height: 18px;
  }

  .payflow-gateway-option__name {
    font-size: 9px;
  }
}
</style>
